import styled from '@emotion/styled';
import { rem } from '@triumph/design-system';

export const SectionLabel = styled.label`
    font-size: ${rem(16)};
    font-weight: 500;
    margin-bottom: ${rem(8)};
    display: block;
`;

export const SectionHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(8)};
    margin-bottom: ${rem(8)};
`;

export const SectionHeader = styled.h3`
    font-size: ${rem(16)};
    font-weight: 600;
    margin: 0;
`;

export const SelectAllLink = styled.button`
    background: none;
    border: none;
    color: #1f5bd6;
    font-size: ${rem(14)};
    cursor: pointer;
    padding: 0;
    text-decoration: underline;

    &:hover {
        color: #1a4fbb;
    }
`;

export const SelectedCountBadge = styled.span`
    background: #e8f0fc;
    color: #1f5bd6;
    border: 1px solid rgba(31, 91, 214, 0.25);
    border-radius: 12px;
    padding: 2px 10px;
    font-size: ${rem(12)};
    font-weight: 500;
`;

export const ProductSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(12)};

    .p-multiselect {
        width: 100%;
        font-size: ${rem(14)};

        .p-multiselect-label {
            font-size: ${rem(14)};
            padding: ${rem(10)} ${rem(12)};
        }
    }

    .p-multiselect.p-multiselect-chip .p-multiselect-token .p-multiselect-token-icon {
        width: 12px;
        height: 12px;
    }

    #docsOnly {
        align-items: center;
    }

    #docsOnly > div:last-child {
        font-size: 13px;
    }
`;

export const RequirementList = styled.div`
    display: flex;
    flex-direction: column;
    background: #fafbfd;
    border: 1px solid rgba(10, 30, 58, 0.08);
    border-radius: 6px;
    overflow: hidden;
`;

export const RequirementRow = styled.label<{ checked?: boolean }>`
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: ${rem(10)};
    padding: ${rem(10)} ${rem(12)};
    cursor: pointer;
    background: ${({ checked }) => (checked ? '#e8f0fc' : 'transparent')};

    & + & {
        border-top: 1px solid rgba(10, 30, 58, 0.08);
    }

    &:hover {
        background: ${({ checked }) => (checked ? '#dbe7fa' : '#f1f4fa')};
    }
`;

export const RequirementLabel = styled.span`
    font-size: ${rem(14)};
    color: #0a1e3a;
`;

type StatusBadgeProps = {
    tone: 'red' | 'green' | 'none';
};

export const StatusBadge = styled.span<StatusBadgeProps>`
    display: ${({ tone }) => (tone === 'none' ? 'none' : 'inline-flex')};
    align-items: center;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: ${rem(11)};
    font-weight: 500;
    text-transform: capitalize;

    background: ${({ tone }) => (tone === 'red' ? '#fde8e8' : '#e6f6ec')};
    color: ${({ tone }) => (tone === 'red' ? '#b42318' : '#067647')};
    border: 1px solid
        ${({ tone }) => (tone === 'red' ? 'rgba(180, 35, 24, 0.25)' : 'rgba(6, 118, 71, 0.25)')};
`;

export const MessageTextArea = styled.textarea<{ disabled?: boolean; hasError?: boolean }>`
    width: 100%;
    box-sizing: border-box;
    min-height: ${rem(140)};
    padding: ${rem(10)} ${rem(12)};
    border: 1px solid ${({ hasError }) => (hasError ? '#b42318' : 'rgba(10, 30, 58, 0.15)')};
    border-radius: 6px;
    font-family: inherit;
    font-size: ${rem(14)};
    line-height: 1.5;
    resize: vertical;
    background: ${({ disabled }) => (disabled ? '#f5f8fd' : '#ffffff')};
    color: #0a1e3a;

    &:focus {
        outline: none;
        border-color: ${({ disabled, hasError }) =>
            hasError ? '#b42318' : disabled ? 'rgba(10, 30, 58, 0.15)' : '#1f5bd6'};
    }
`;

export const EditMessageLink = styled.button`
    background: none;
    border: none;
    color: #1f5bd6;
    font-size: ${rem(14)};
    cursor: pointer;
    padding: ${rem(6)} 0 0 0;
    text-decoration: underline;
    align-self: flex-start;

    &:hover {
        color: #1a4fbb;
    }
`;

export const ReadOnlyEmailRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(4)};
    font-size: ${rem(14)};
    color: #0a1e3a;

    & > span {
        color: #6b7a92;
        font-size: ${rem(12)};
    }
`;

export const FieldError = styled.div`
    color: #b42318;
    font-size: ${rem(12)};
    margin-top: ${rem(4)};
`;

export const PreviewSection = styled.div<{ stale?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${rem(8)};
    border: 1px solid rgba(10, 30, 58, 0.1);
    border-radius: 6px;
    padding: ${rem(12)};
    background: #fafbfd;
    opacity: ${({ stale }) => (stale ? 0.6 : 1)};
    transition: opacity 150ms ease;
`;

export const PreviewSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${rem(8)};
`;

export const StalePreviewBadge = styled.span`
    background: #fff4e5;
    color: #b85c00;
    border: 1px solid rgba(184, 92, 0, 0.3);
    border-radius: 12px;
    padding: 2px 10px;
    font-size: ${rem(12)};
    font-weight: 500;
`;

export const ActiveRequestsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(8)};
    padding: ${rem(12)};
    background: hsl(48 100% 96% / 1);
    border: 1px solid hsl(48 100% 70% / 0.5);
    border-radius: 6px;
`;

export const ActiveRequestCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(4)};
    padding: ${rem(10)} ${rem(12)};
    background: white;
    border: 1px solid hsl(48 100% 70% / 0.4);
    border-radius: 4px;
    font-size: ${rem(13)};
    color: #0a1e3a;
`;

export const ActiveRequestMeta = styled.div`
    display: flex;
    gap: ${rem(8)};
    align-items: center;
    flex-wrap: wrap;
`;

export const ActiveRequestProductTag = styled.span`
    background: hsl(48 100% 80% / 1);
    color: black;
    border-radius: ${rem(10)};
    padding: 1px 8px;
    font-size: ${rem(11)};
    font-weight: 600;
`;

export const ActiveRequestDate = styled.span`
    color: #6b7a92;
    font-size: ${rem(11)};
`;

export const ActiveRequestMessage = styled.p`
    margin: 0;
    font-size: ${rem(12)};
    color: #0a1e3a;
    white-space: pre-wrap;
    max-height: ${rem(80)};
    overflow-y: auto;
    border-top: 1px solid rgba(10, 30, 58, 0.08);
    padding-top: ${rem(6)};
`;

export const SendNewHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${rem(8)};
    padding-top: ${rem(4)};
    border-top: 2px solid rgba(10, 30, 58, 0.08);
`;

export const HidePreviewLink = styled.button`
    background: none;
    border: none;
    color: #1f5bd6;
    font-size: ${rem(13)};
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    align-self: flex-start;
`;

export const DeclinedProductWarning = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(6)};
    padding: ${rem(10)} ${rem(12)};
    background: #fef3c7;
    border: 1px solid #fde68a;
    border-radius: 6px;
    font-size: ${rem(13)};
    color: #92400e;
`;

export const DeclinedProductWarningRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(2)};

    & + & {
        padding-top: ${rem(6)};
        border-top: 1px solid rgba(146, 64, 14, 0.2);
    }
`;

export const DeclinedProductWarningMeta = styled.span`
    font-size: ${rem(11)};
    opacity: 0.85;
`;
